/**
 * displayState.js — Separates backend lifecycle `status` from visual display state.
 *
 * Backend model:
 *   status : 'active' | 'inactive' | 'archived' | 'maintenance'
 *   online : boolean (connectivity)
 *
 * Frontend display state (for map / live views):
 *   'moving' | 'idle' | 'stopped' | 'offline' | 'alert'
 */

/* ─── Display-state colors (map / badge / dot) ──── */
export const DISPLAY_STATE_COLORS = {
  moving:  '#FACC15',
  idle:    '#F59E0B',
  stopped: '#25e14e',
  offline: '#2f72f9',
  alert:   '#EF4444',
}

/**
 * Derive visual display state from an entity's live data.
 * Reads `online` boolean for connectivity, `speed` for motion.
 * Never reads `status` — that field is lifecycle-only.
 *
 * @param {Object} entity  asset / shadow / enriched object
 * @returns {'moving'|'idle'|'stopped'|'offline'|'alert'}
 */
export function resolveDisplayState(entity) {
  if (!entity) return 'offline'
  if (entity.online === false) return 'offline'
  if (entity._alert) return 'alert'
  if ((entity.speed ?? 0) > 2) return 'moving'
  if (entity.stopped) return 'stopped'
  return 'idle'
}

/* ─── Backend lifecycle status helpers ────────────── */

/** Canonical status enum */
export const STATUS_ENUM = ['active', 'inactive', 'archived', 'maintenance']

const STATUS_META = {
  active:      { color: 'success', label: 'Active',      icon: 'mdi-check-circle' },
  inactive:    { color: 'grey',    label: 'Inactive',    icon: 'mdi-minus-circle' },
  maintenance: { color: 'warning', label: 'Maintenance', icon: 'mdi-wrench' },
  archived:    { color: 'error',   label: 'Archived',    icon: 'mdi-archive' },
}

/** Get display metadata for a backend lifecycle status value. */
export function getStatusMeta(status) {
  return STATUS_META[status] || STATUS_META.inactive
}

/** Get color string for a backend lifecycle status (for VChip etc.). */
export function getStatusColor(status) {
  return getStatusMeta(status).color
}

/** Get label string for a backend lifecycle status. */
export function getStatusLabel(status) {
  return getStatusMeta(status).label
}

/* ─── Connectivity helpers ───────────────────────── */

/**
 * Get color / label for a device or asset's connectivity state.
 * @param {boolean} online
 * @returns {{ color: string, label: string, icon: string }}
 */
export function getConnectivityMeta(online) {
  return online
    ? { color: 'success', label: 'Online',    icon: 'mdi-check-circle' }
    : { color: 'grey',    label: 'No signal', icon: 'mdi-wifi-off' }
}
